import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';

// React Error Boundary for ResizeObserver errors
class ResizeObserverErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    if (error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      return { hasError: false }; // Don't show error UI for ResizeObserver errors
    }
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Suppress ResizeObserver errors
      return;
    }
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong.</div>;
    }

    return this.props.children;
  }
}

// Ultra-aggressive ResizeObserver error suppression
const suppressResizeObserverError = (error: any) => {
  if (!error) return false;
  const errorMessage = error?.message || error?.toString() || '';
  return errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
         errorMessage.includes('ResizeObserver') ||
         errorMessage.includes('undelivered notifications');
};

// Override all console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

console.error = (...args) => {
  if (suppressResizeObserverError(args[0])) return;
  originalConsoleError.apply(console, args);
};

console.warn = (...args) => {
  if (suppressResizeObserverError(args[0])) return;
  originalConsoleWarn.apply(console, args);
};

console.log = (...args) => {
  if (suppressResizeObserverError(args[0])) return;
  originalConsoleLog.apply(console, args);
};

// Global error handlers
window.addEventListener('error', (event) => {
  if (suppressResizeObserverError(event.error) || suppressResizeObserverError(event.message)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (suppressResizeObserverError(event.reason)) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Additional error event listeners
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('ResizeObserver')) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
}, true);

// Override the global error handler completely
const originalWindowError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (suppressResizeObserverError(error) || suppressResizeObserverError(message)) {
    return true;
  }
  if (originalWindowError) {
    return originalWindowError.call(window, message, source, lineno, colno, error);
  }
  return false;
};

// Override React's development error overlay
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (suppressResizeObserverError(args[0])) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

// Additional error suppression for React's error handling
const originalHandleError = (window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
if (originalHandleError && originalHandleError.onError) {
  const originalOnError = originalHandleError.onError;
  originalHandleError.onError = (error: any) => {
    if (suppressResizeObserverError(error)) {
      return;
    }
    originalOnError.call(originalHandleError, error);
  };
}

// Completely replace ResizeObserver with a no-op implementation
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class NoOpResizeObserver {
  private callback: ResizeObserverCallback;
  private observedElements: Set<Element> = new Set();

  constructor(callback: ResizeObserverCallback) {
    this.callback = callback;
  }

  observe(target: Element, options?: ResizeObserverOptions) {
    this.observedElements.add(target);
    // Do nothing - completely disable ResizeObserver functionality
  }

  unobserve(target: Element) {
    this.observedElements.delete(target);
    // Do nothing
  }

  disconnect() {
    this.observedElements.clear();
    // Do nothing
  }
} as any;

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ResizeObserverErrorBoundary>
      <Router>
        <App />
      </Router>
    </ResizeObserverErrorBoundary>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
